import * as XLSX from 'xlsx';
import * as budgetRepo from "./BudgetImportApiRepository";
import { Autocomplete, CircularProgress, TextField } from "@mui/material";
import { ConfirmationModal, ModalPredefinedSize, ModalProvider, useModal } from "../../generalcomponents/ModalProvider";
import { ToastProvider, useToast } from "../../generalcomponents/ToasterProvider";
import { useEffect, useRef, useState } from "react";
import _ from 'lodash';
import { useTranslation } from "react-i18next";
import { LoadSpinnerProvider, useLoadSpinner } from '../../generalcomponents/LoadSpinnerProvider';

// -----------------------------------------------------------------------------
// Types and classes
// -----------------------------------------------------------------------------
type WizardState =
    | { stateName: "selectingFile" }
    | { stateName: "mappingColumns" }
    | { stateName: "editingFileData" } // Currently unused. See related doc comment for function DataEditingForm for more info

enum BudgetImportType {
    SalesBudget = "SalesBudget",
    PurchaseBudget = "PurchaseBudget"
}

type SheetColumn = { id: number, name: string }

export type SalesBudgetRow = {
    budgetTypeId: number,
    collectionCode: string,
    itemGroupCode: string,
    locationNumber: string,
    quantity: number,
    salesAmount: number,
    startDate: Date,
    endDate: Date
}

export type PurchaseBudgetRow = {
    budgetTypeId: number,
    collectionCode: string,
    itemGroupCode: string,
    locationNumber: number,
    orderedCost: number,
    orderedQuantity: number,
    purchaseCost: number,
    quantity: number
}

type BudgetRowsValidationResult = {
    ColumnName: String,
    ErrorDescription: String
}

// -----------------------------------------------------------------------------
// Exported "public" function components
// -----------------------------------------------------------------------------
/**
 * Component for selecting an excel file containing budget data, processing it,
 * and sending the resulting data to the backend
 */
export function BudgetImport() {
    return (
        <ToastProvider>
            <LoadSpinnerProvider>
                <ModalProvider sizePreset={ModalPredefinedSize.default}>
                    <BudgetImportInner />
                </ModalProvider>
            </LoadSpinnerProvider>
        </ToastProvider>
    );
}

/**
 * (Raw component version without wrapper components)
 * Component for selecting an excel file containing budget data, processing it,
 * and sending the resulting data to the backend
 */
export function BudgetImportInner() {
    const [currentState, setCurrentState] = useState<WizardState>({ stateName: "selectingFile" });
    const [sheetData, setSheetData] = useState<any>({});
    const [sheetColumns, setSheetColumns] = useState<SheetColumn[]>([]);
    const [selectedFileType, setSelectedFileType] = useState<BudgetImportType>(BudgetImportType.SalesBudget);
    const [fileSelectorKey, setFileSelectorKey] = useState(0);

    const { showError, showSuccess } = useToast();
    const { t } = useTranslation();

    const receiveFile = async (f: File) => {
        console.log("File received: ", f);
        try {
            const { sheetData, columns } = await handleImportFile(f, t, showError);
            setSheetData(sheetData);
            setSheetColumns(columns);
            changeStateToMappingColumns();
        } catch (e) {
            showError(String(e instanceof Error ? e.message : e));
        }
    };

    const receiveBudgetRows = async (rows: SalesBudgetRow[] | PurchaseBudgetRow[]) => {
        console.log("Rows are: ", rows);
        if (selectedFileType === BudgetImportType.SalesBudget) {
            await budgetRepo.saveSalesBudgetImport(rows as SalesBudgetRow[])
                .then(() => showSuccess(t("budgetimport.importSuccess"), null))
                .catch((e) => showError(String(e)));
        } else if (selectedFileType === BudgetImportType.PurchaseBudget) {
            await budgetRepo.savePurchaseBudgetImport(rows as PurchaseBudgetRow[])
                .then(() => showSuccess(t("budgetimport.importSuccess"), null))
                .catch((e) => showError(String(e)));
        }
    };

    const changeStateTo = (stateName: WizardState["stateName"]) => {
        setCurrentState({ stateName });
    };

    const changeStateToMappingColumns = () => { changeStateTo("mappingColumns"); };

    const resetToFileSelect = () => {
        setSheetData({});
        setSheetColumns([]);
        setSelectedFileType(BudgetImportType.SalesBudget);
        setFileSelectorKey(fileSelectorKey + 1);
        changeStateTo("selectingFile");
    };

    return (
        <div>
            <div style={{ marginLeft: "8px", marginBottom: "8px" }}>
                <StateBreadcrumbs state={currentState} onStateChangeRequested={resetToFileSelect} />
            </div>
            {(["selectingFile", "mappingColumns"].includes(currentState.stateName)) &&
                <div style={{ "marginBottom": "8px" }}>
                    <ExcelFileSelector
                        key={fileSelectorKey}
                        onFileChange={receiveFile}
                        onFileTypeChange={setSelectedFileType}
                        currentWizardState={currentState}
                    />
                </div>
            }
            {(currentState.stateName == "mappingColumns") &&
                <div>
                    <ColumnMappingForm
                        sheetData={sheetData}
                        sheetColumns={sheetColumns}
                        selectedImportType={selectedFileType}
                        onInformationMapped={receiveBudgetRows} />
                </div>
            }
            {(currentState.stateName == "editingFileData") &&
                <div>
                    {/*Currently unused*/}
                    <DataEditingForm />
                </div>
            }
        </div>
    );
}

// -----------------------------------------------------------------------------
// "Private" function components
// -----------------------------------------------------------------------------
/**
 * Displays the name of the current and past stages of the importing process
 */
function StateBreadcrumbs({ state, onStateChangeRequested }:
    {
        state: WizardState,
        onStateChangeRequested: () => void
    }) {
    const { t, ready } = useTranslation();
    const { openModal, closeModal } = useModal();

    if (!ready) { return null; }

    const clickableVisualStyle = {
        fontWeight: "bold",
        fontSize: "small",
        marginLeft: "4px",
        color: "blue",
        cursor: "pointer"
    };

    const nonClickableVisualStyle = {
        fontWeight: "bold",
        fontSize: "small",
        marginLeft: "4px"
    };

    const handleConfirmationModalResponse = (response: boolean) => {
        console.log("BudgetImport.tsx received modal response: ", response);
        if (response == true) {
            onStateChangeRequested();
        }
    };

    const handleClickingFileSelectionStage = () => {
        if (!["mappingColumns", "editingFileData"].includes(state.stateName)) {
            return;
        }

        openModal(
            (<ConfirmationModal
                header={t("budgetimport.confirmRevert")}
                message={t("budgetimport.shouldDiscardAndRevertWarning")}
                onModalResponse={handleConfirmationModalResponse}
            />)
        );
    };

    return (
        <>
            <span
                style={
                    ["mappingColumns", "editingFileData"].includes(state.stateName) ?
                        clickableVisualStyle :
                        nonClickableVisualStyle
                }
                onClick={handleClickingFileSelectionStage}>
                <label style={{ fontSize: "20px" }}> » </label> {t('budgetimport.selectingFile')}
            </span>
            {(["mappingColumns", "editingFileData"].includes(state.stateName)) &&
                <span style={{ fontWeight: "bold", fontSize: "small", marginLeft: "4px" }}>
                    <label style={{ fontSize: "20px" }}> » </label> {t('budgetimport.mappingColumns')}
                </span>
            }
            {(["editingFileData"].includes(state.stateName)) &&
                <span style={{ fontWeight: "bold", fontSize: "small", marginLeft: "4px" }}>
                    <label style={{ fontSize: "20px" }}> » </label> {t('budgetimport.editingFileData')}
                </span>
            }
        </>
    );
}

/**
 * Handles selecting the file that should be processed
 */
function ExcelFileSelector({ onFileChange, onFileTypeChange, currentWizardState }:
    {
        onFileChange: (file: File) => void
        onFileTypeChange: (fileType: BudgetImportType) => void
        currentWizardState: WizardState
    }) {
    const { t, ready } = useTranslation();
    const { showError } = useToast();
    const [selectedFileName, setSelectedFileName] = useState("");
    const [selectedImportFileType, setSelectedImportFileType] = useState<BudgetImportType>(BudgetImportType.SalesBudget);

    if (!ready) { return null; }

    const onInputFileChanged = (input: React.ChangeEvent<HTMLInputElement>) => {
        const files = input.target.files;

        if (files == null) { return; }

        setSelectedFileName(files[0].name);
        onFileChange(files[0]);
        onFileTypeChange(selectedImportFileType);
        input.target.value = "";
    };

    return (
        <div>
            <div className="form-group row">
                <label className="col-sm-2 control-label">{t("productimport.fileType")}:</label>
                <div className="col-sm-10">
                    <div className="radio">
                        <label className="i-checks">
                            <input
                                name="importType"
                                type="radio"
                                checked={selectedImportFileType === BudgetImportType.SalesBudget}
                                onChange={() => setSelectedImportFileType(BudgetImportType.SalesBudget)}
                                disabled={currentWizardState.stateName !== "selectingFile"}
                            />
                            <i> </i>
                            {t("budgetimport.salesBudgetImport")}
                        </label>
                    </div>
                    <div className="radio">
                        <label className="i-checks">
                            <input
                                name="importType"
                                type="radio"
                                checked={selectedImportFileType === BudgetImportType.PurchaseBudget}
                                onChange={() => setSelectedImportFileType(BudgetImportType.PurchaseBudget)}
                                disabled={currentWizardState.stateName !== "selectingFile"}
                            />
                            <i> </i>
                            {t("budgetimport.purchaseBudgetImport")}
                        </label>
                    </div>
                </div>
            </div>
            <div className="form-group row">
                <label className="col-sm-2 control-label">{t("productimport.file")}:</label>
                <div className="col-sm-10">
                    <div>
                        <label className="btn btn-default btn-file">
                            {t("stock.readfile")}
                            <input
                                id="xlf"
                                type="file"
                                name="xlfile"
                                style={{ display: "none" }}
                                onChange={onInputFileChanged} />
                        </label>
                        {(selectedFileName != "") &&
                            <label style={{ marginLeft: "8px" }}>
                                {selectedFileName}
                            </label>
                        }
                    </div>
                    {selectedFileName === "" &&
                        <div style={{ marginTop: "8px" }}>
                            <button
                                className="btn btn-default"
                                type="button"
                                onClick={() => {
                                    const download = selectedImportFileType === BudgetImportType.SalesBudget
                                        ? budgetRepo.downloadSalesBudgetTemplate
                                        : budgetRepo.downloadPurchaseBudgetTemplate;
                                    download().catch((e) => showError(String(e)));
                                }}>
                                {t("budgetimport.downloadTemplate")}
                            </button>
                        </div>
                    }
                </div>
            </div>
        </div>
    );
}

/**
 * The part that selects columns from the excel file and maps them
 * to the corresponding field on the request message
 */
function ColumnMappingForm({ sheetData, sheetColumns, selectedImportType, onInformationMapped }:
    {
        sheetData: any,
        sheetColumns: SheetColumn[],
        selectedImportType: BudgetImportType,
        onInformationMapped: (rows: SalesBudgetRow[] | PurchaseBudgetRow[]) => Promise<void>
    }) {
    const { t, ready } = useTranslation();
    const { showError } = useToast();
    const [budgetTypes, setBudgetTypes] = useState<budgetRepo.BudgetType[]>();
    const [selectedBudgetType, setSelectedBudgetType] = useState<budgetRepo.BudgetType>();
    const [validItemGroupCodes, setValidItemGroupCodes] = useState<string[]>([]);
    const [validLocationNumbers, setValidLocationNumbers] = useState<number[]>([]);
    const [validCollectionCodes, setValidCollectionCodes] = useState<number[]>([]);
    const [validationErrors, setValidationErrors] = useState<BudgetRowsValidationResult[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const loadSpinner = useLoadSpinner();

    const maxFormWidth: number = 700;
    const columnDisplayWidth: number = 300;
    const columnHorizontalMargin: number = 8;

    // Don't render before translations are ready
    if (!ready) { return null; }

    useEffect(() => {
        const loadTypes = async () => {
            let types = [];
            switch (selectedImportType) {
                case BudgetImportType.SalesBudget:
                    types = await budgetRepo.getSalesBudgetTypes();
                    break;
                case BudgetImportType.PurchaseBudget:
                    types = await budgetRepo.getPurchaseBudgetTypes();
                    break;
            }
            setBudgetTypes(types);
            if (types.length > 0) setSelectedBudgetType(types[0]);
        };
        loadSpinner.start();
        loadTypes()
            .catch((e) => showError(String(e)))
            .finally(() => { loadSpinner.stop(); });
    }, [selectedImportType]);

    useEffect(() => {
        budgetRepo.getItemGroupCodes()
            .then(setValidItemGroupCodes)
            .catch((e) => showError(String(e)));
    }, []);

    useEffect(() => {
        budgetRepo.getLocationNumbers()
            .then(setValidLocationNumbers)
            .catch((e) => showError(String(e)));
    }, []);

    useEffect(() => {
        budgetRepo.getCollectionCodes()
            .then(setValidCollectionCodes)
            .catch((e) => showError(String(e)));
    }, []);

    // { fieldName, translationLabel }
    const salesBudgetAvailableInputs: Record<string, string> = {
        // "collectionCode": "budgetimport.collectionCode", // TODO: Set this to current collection in the backend
        // "itemGroupCode": "budget.itemGroupCode", // TODO: Seemingly arbitrary. Set to whatever in the backend
        "locationNumber": "budgetimport.locationNumber",
        // "quantity": "budget.quantity", // TODO: Set to 0 in the backend
        "salesAmount": "budget.salesAmount",
        "startDate": "budgetimport.date"
        // "endDate": "budget.enddate" // TODO: Set to the same Date as the startDate in the backend
    };

    // { fieldName, translationLabel }
    const purchaseBudgetAvailableInputs: Record<string, string> = {
        "collectionCode": "budgetimport.collectionCode",
        "itemGroupCode": "budget.itemGroupCode",
        "locationNumber": "budgetimport.locationNumber",
        // "orderedCost": "budget.orderedCost",
        // "orderedQuantity": "budget.orderedQuantity",
        "purchaseCost": "budget.purchaseCost",
        "quantity": "budget.quantity"
    };

    const availableInputs: Record<BudgetImportType, Record<string, string>> = {
        "SalesBudget": salesBudgetAvailableInputs,
        "PurchaseBudget": purchaseBudgetAvailableInputs
    };

    // { fieldName, excelColumnName }
    const selectedInputs = useRef<Record<string, string>>({
        "collectionCode": "",
        "itemGroupCode": "",
        "locationNumber": "",
        "orderedCost": "",
        "orderedQuantity": "",
        "purchaseCost": "",
        "quantity": "",
        "salesAmount": "",
        "startDate": "",
        "endDate": ""
    });

    const [selectedColumnObjects, setSelectedColumnObjects] = useState<Record<string, SheetColumn | null>>({});

    const updateColumnMapping = (key: string, value: string, column: SheetColumn | null) => {
        selectedInputs.current[key] = value;
        setSelectedColumnObjects((prev) => ({ ...prev, [key]: column }));
    };

    // Currently does validation, then if the validation is Ok, sends
    // the information up the component tree
    const confirmColumnMapping = async () => {
        const errors: BudgetRowsValidationResult[] = [];
        if (!selectedBudgetType) {
            errors.push({ ColumnName: "budgetType", ErrorDescription: t("budgetimport.errors.budgetTypeNotSelected") });
        }
        if (selectedImportType === BudgetImportType.SalesBudget) {
            const rows = mapSalesColumns(selectedInputs.current, sheetData);
            errors.push(...validateSalesBudgetRows(rows, validLocationNumbers, t));
            if (errors.length > 0) { setValidationErrors(errors); return; }
            setValidationErrors([]);
            setIsSubmitting(true);
            await onInformationMapped(rows).finally(() => setIsSubmitting(false));
        } else if (selectedImportType === BudgetImportType.PurchaseBudget) {
            const rows = mapPurchaseColumns(selectedInputs.current, sheetData);
            errors.push(...validatePurchaseBudgetRows(rows, validItemGroupCodes, validLocationNumbers, validCollectionCodes, !!selectedInputs.current["itemGroupCode"], t));
            if (errors.length > 0) { setValidationErrors(errors); return; }
            setValidationErrors([]);
            setIsSubmitting(true);
            await onInformationMapped(rows).finally(() => setIsSubmitting(false));
        }
    };

    // SalesBudget mapping to backend payload object
    function mapSalesColumns(selectedInputs: Record<string, string>, sheetData: any): SalesBudgetRow[] {
        const filteredSelectedInputs = Object.fromEntries(
            Object.entries(selectedInputs).filter(([_, selectedMapping]) => selectedMapping !== "")
        );

        const dateFields = ["startDate", "endDate"];
        const numericFields = ["locationNumber", "quantity"];
        const rows: SalesBudgetRow[] = sheetData.map((sheet: any) =>
            /* eslint-disable-next-line implicit-arrow-linebreak */
            Object.fromEntries(
                Object.entries(filteredSelectedInputs).map(([propName, columnHeader]) => {
                    const raw = sheet[columnHeader];
                    let value: unknown;
                    if (dateFields.includes(propName)) {
                        value = toUtcMidnight(raw as Date);
                    } else if (propName === "salesAmount") {
                        const parsed = Number(raw);
                        value = isNaN(parsed) ? 0 : parsed;
                    } else if (numericFields.includes(propName)) {
                        value = Number(raw);
                    } else {
                        value = String(raw ?? "");
                    }
                    return [propName, value] as [string, unknown];
                })
            ) as unknown as SalesBudgetRow
        );

        rows.forEach((r) => { r.budgetTypeId = selectedBudgetType?.id ?? 0; });

        return rows;
    }

    // PurchaseBudget mapping to backend payload object
    function mapPurchaseColumns(selectedInputs: Record<string, string>, sheetData: any): PurchaseBudgetRow[] {
        const filteredSelectedInputs = Object.fromEntries(
            Object.entries(selectedInputs).filter(([_, selectedMapping]) => selectedMapping !== "")
        );

        const rows: PurchaseBudgetRow[] = sheetData.map((sheet: any) =>
            /* eslint-disable-next-line implicit-arrow-linebreak */
            Object.fromEntries(
                Object.entries(filteredSelectedInputs).map(([propName, columnHeader]) => [propName, String(sheet[columnHeader] ?? "")] as [string, string])
            ) as unknown as PurchaseBudgetRow
        );

        rows.forEach((r) => { r.budgetTypeId = selectedBudgetType?.id ?? 0; });

        return rows;
    }

    // ----- Sub-components -----
    // A single, general selection, label + input box combination
    function ColumnMappingInputRow({ fieldName, label, selectableColumns, value, onColumnMappingSelected }:
        {
            fieldName: string,
            label: string,
            selectableColumns: SheetColumn[],
            value: SheetColumn | null,
            onColumnMappingSelected: (key: string, value: string, column: SheetColumn | null) => void
        }) {

        const selectColumn = (_: React.SyntheticEvent, value: SheetColumn | null) => {
            var columnName = value?.name ?? "";
            onColumnMappingSelected(fieldName, columnName, value);
        };

        return (
            <div>
                <label>
                    {t(label)}:
                </label>
                <Autocomplete
                    value={value}
                    onChange={selectColumn}
                    size="medium"
                    options={selectableColumns}
                    getOptionLabel={(p) => p.name}
                    sx={{ width: columnDisplayWidth }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            slotProps={{
                                htmlInput: {
                                    ...params.inputProps,
                                    style: { fontSize: "12px" }
                                }
                            }}
                        />
                    )}
                />
            </div>
        );
    }

    // Specific selection (currently only selects budgetType)
    function SingleSelectRow({ selectableValues, onValueSelected }: {
        selectableValues: budgetRepo.BudgetType[],
        onValueSelected: (selectedBudgetType: budgetRepo.BudgetType | null) => void
    }) {

        const selectValue = (_: React.SyntheticEvent, value: budgetRepo.BudgetType | null) => {
            console.log("SingleSelectRow value selected: ", value);
            onValueSelected(value);
        };

        return (
            <div>
                <label>
                    {t("budgetimport.budgettype")}:
                </label>
                <Autocomplete
                    value={selectedBudgetType ?? null}
                    onChange={selectValue}
                    size="medium"
                    options={selectableValues}
                    getOptionLabel={(p) => t(`budget.systemtype.${p.budgetDescription}`)}
                    sx={{ width: columnDisplayWidth }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            slotProps={{
                                htmlInput: {
                                    ...params.inputProps,
                                    style: { fontSize: "12px" }
                                }
                            }}
                        />
                    )}
                />
            </div>
        );

    }

    const inputLabelRowStyle = {
        marginBottom: "8px",
        flexBasis: "50%",
        minWidth: (columnDisplayWidth + columnHorizontalMargin) + "px"
    };

    return (
        <div style={{ maxWidth: maxFormWidth + "px" }}>
            <div style={{ display: "flex", flexWrap: "wrap" }}>
                <div
                    key="budgettype.budgettype"
                    style={inputLabelRowStyle}>
                    <SingleSelectRow
                        selectableValues={budgetTypes ?? []}
                        onValueSelected={(b) => setSelectedBudgetType(b ?? undefined)}
                    />
                </div>
                {Object.entries(availableInputs[selectedImportType])
                    .map(([fieldName, translationLabel]) => (
                        <div
                            key={fieldName}
                            style={inputLabelRowStyle}>
                            <ColumnMappingInputRow
                                fieldName={fieldName}
                                label={translationLabel}
                                selectableColumns={sheetColumns}
                                value={selectedColumnObjects[fieldName] ?? null}
                                onColumnMappingSelected={updateColumnMapping}
                            />
                        </div>
                    ))
                }
            </div>
            <button
                onClick={confirmColumnMapping}
                className="btn btn-primary"
                type="button"
                disabled={isSubmitting}
                style={{ "marginTop": "8px" }}>
                {isSubmitting
                    ? <CircularProgress size={16} color="inherit" style={{ verticalAlign: "middle" }} />
                    : t("budgetimport.acceptcolumnmapping")
                }
            </button>
            {validationErrors.length > 0 &&
                <div style={{ marginTop: "8px", maxWidth: "650px", backgroundColor: "#e8563c", border: "3px solid #e8765c", color: "#fff", borderRadius: "15px", padding: "5px 15px", fontSize: "1.1em" }}>
                    <ul style={{ margin: 0, paddingLeft: "16px" }}>
                        {validationErrors.map((e, i) => (
                            <li key={i}>{String(e.ErrorDescription)}</li>
                        ))}
                    </ul>
                </div>
            }
        </div>
    );
}

/**
 * The part of the UI that enables the user to edit the incomin data before sending it
 * This is currently only a stub since we decided to provide the user with a template excel
 * file instead to use an external editor to fill in the required data
 */
function DataEditingForm() {
    return (
        <>
            Data editing form goes here
        </>
    );
}

// ----------------------------------------------------------------------------
// (Non-component) Functions
// ----------------------------------------------------------------------------
/**
 * Main usage of the XLSX plugin to process the selected excel file
 */
function handleImportFile(file: File, t: (key: string) => string, showError: (message: string) => void): Promise<{
    sheetData: any,
    columns: SheetColumn[]
}> {
    const promise = new Promise<{ sheetData: any, columns: SheetColumn[] }>((resolve, reject) => {

        const reader = new FileReader();

        reader.onload = (e: ProgressEvent<FileReader>) => {
            let sheetData: any = [];
            let columns: SheetColumn[] = [];
            // loadSpinnerService.start('mainSpinner');

            try {
                let data = "";

                const result = e.target?.result;
                if (!(result instanceof ArrayBuffer)) {
                    console.log("Cannot parse the file result as an array buffer");
                    return;
                }

                const bytes = new Uint8Array(result);
                const length = bytes.byteLength;
                for (let i = 0; i < length; i++) {
                    data += String.fromCharCode(bytes[i]);
                }

                // ------------ XLSX ------------
                // Read Excel data into a workbook object representation.
                const workbook = XLSX.read(data, { type: 'binary', cellDates: true });

                // Getting the name of the first sheet in the workbook.
                const firstSheetName = workbook.SheetNames[0];

                // Getting the sheet based on the firstSheetName.
                const workSheet = workbook.Sheets[firstSheetName];

                // The JSon representation of the worksheet returned by js-xlsx
                const JSonAdaptedSheetData = XLSX.utils.sheet_to_json<any>(workSheet);

                sheetData = cleanColumnNames(JSonAdaptedSheetData);
                columns = extractImportFileColumnNames(JSonAdaptedSheetData);

                console.log("JsonAdaptedSheetData is: ", JSonAdaptedSheetData);

            } catch (error) {
                console.log(error);
                showError(t('productimport.errorWhileReadingImportFile'));
            } finally {
                // loadSpinnerService.stop('mainSpinner');
            }
            resolve({ sheetData, columns });
        };

        reader.onerror = () => reject(new Error(t("budgetimport.errors.fileReadError")));

        // From MDN: "readAsBinaryString is deprecated and readAsArrayBuffer() should be used instead."
        reader.readAsArrayBuffer(file);
    });

    return promise;
}

/**
 *  Removed special characters from the column names
 */
function cleanColumnNames(JSonAdaptedSheetData: any) {
    if (JSonAdaptedSheetData.length !== 0) {
        for (let sheetIndex = 0; sheetIndex < JSonAdaptedSheetData.length; sheetIndex++) {
            let sheetData: Record<string, any> = JSonAdaptedSheetData[sheetIndex];
            const keys = Object.keys(sheetData);

            for (let keyIndex = 0; keyIndex < keys.length; keyIndex++) {
                const key = keys[keyIndex];
                const origKey = key;
                const keyStr = normalizeString(key);
                //Check if the key has changed after normalization, if yes replace the column with the new name
                if (origKey !== keyStr) {
                    sheetData[keyStr] = sheetData[origKey];
                    const index = keys.indexOf(origKey);
                    keys[index] = keyStr;
                    sheetData = Object.fromEntries(
                        keys.map((key) => [key, sheetData[key]])
                    );
                }
            }

            JSonAdaptedSheetData[sheetIndex] = sheetData;
        }
        return JSonAdaptedSheetData;
    }
}

/**
 *  Removes unprocessable special characters from a string
 */
function normalizeString(str: string): string {
    //Make sure string contains only letters, numbers and some special characters
    let returnValue = str;
    const r = /[^ ()/\\0-9\u0041-\u005A\u0061-\u007A\u00AA\u00B5\u00BA\u00C0-\u00D6\u00D8-\u00F6\u00F8-\u02C1\u02C6-\u02D1\u02E0-\u02E4\u02EC\u02EE\u0370-\u0374\u0376\u0377\u037A-\u037D\u0386\u0388-\u038A\u038C\u038E-\u03A1\u03A3-\u03F5\u03F7-\u0481\u048A-\u0527\u0531-\u0556\u0559\u0561-\u0587\u05D0-\u05EA\u05F0-\u05F2\u0620-\u064A\u066E\u066F\u0671-\u06D3\u06D5\u06E5\u06E6\u06EE\u06EF\u06FA-\u06FC\u06FF\u0710\u0712-\u072F\u074D-\u07A5\u07B1\u07CA-\u07EA\u07F4\u07F5\u07FA\u0800-\u0815\u081A\u0824\u0828\u0840-\u0858\u08A0\u08A2-\u08AC\u0904-\u0939\u093D\u0950\u0958-\u0961\u0971-\u0977\u0979-\u097F\u0985-\u098C\u098F\u0990\u0993-\u09A8\u09AA-\u09B0\u09B2\u09B6-\u09B9\u09BD\u09CE\u09DC\u09DD\u09DF-\u09E1\u09F0\u09F1\u0A05-\u0A0A\u0A0F\u0A10\u0A13-\u0A28\u0A2A-\u0A30\u0A32\u0A33\u0A35\u0A36\u0A38\u0A39\u0A59-\u0A5C\u0A5E\u0A72-\u0A74\u0A85-\u0A8D\u0A8F-\u0A91\u0A93-\u0AA8\u0AAA-\u0AB0\u0AB2\u0AB3\u0AB5-\u0AB9\u0ABD\u0AD0\u0AE0\u0AE1\u0B05-\u0B0C\u0B0F\u0B10\u0B13-\u0B28\u0B2A-\u0B30\u0B32\u0B33\u0B35-\u0B39\u0B3D\u0B5C\u0B5D\u0B5F-\u0B61\u0B71\u0B83\u0B85-\u0B8A\u0B8E-\u0B90\u0B92-\u0B95\u0B99\u0B9A\u0B9C\u0B9E\u0B9F\u0BA3\u0BA4\u0BA8-\u0BAA\u0BAE-\u0BB9\u0BD0\u0C05-\u0C0C\u0C0E-\u0C10\u0C12-\u0C28\u0C2A-\u0C33\u0C35-\u0C39\u0C3D\u0C58\u0C59\u0C60\u0C61\u0C85-\u0C8C\u0C8E-\u0C90\u0C92-\u0CA8\u0CAA-\u0CB3\u0CB5-\u0CB9\u0CBD\u0CDE\u0CE0\u0CE1\u0CF1\u0CF2\u0D05-\u0D0C\u0D0E-\u0D10\u0D12-\u0D3A\u0D3D\u0D4E\u0D60\u0D61\u0D7A-\u0D7F\u0D85-\u0D96\u0D9A-\u0DB1\u0DB3-\u0DBB\u0DBD\u0DC0-\u0DC6\u0E01-\u0E30\u0E32\u0E33\u0E40-\u0E46\u0E81\u0E82\u0E84\u0E87\u0E88\u0E8A\u0E8D\u0E94-\u0E97\u0E99-\u0E9F\u0EA1-\u0EA3\u0EA5\u0EA7\u0EAA\u0EAB\u0EAD-\u0EB0\u0EB2\u0EB3\u0EBD\u0EC0-\u0EC4\u0EC6\u0EDC-\u0EDF\u0F00\u0F40-\u0F47\u0F49-\u0F6C\u0F88-\u0F8C\u1000-\u102A\u103F\u1050-\u1055\u105A-\u105D\u1061\u1065\u1066\u106E-\u1070\u1075-\u1081\u108E\u10A0-\u10C5\u10C7\u10CD\u10D0-\u10FA\u10FC-\u1248\u124A-\u124D\u1250-\u1256\u1258\u125A-\u125D\u1260-\u1288\u128A-\u128D\u1290-\u12B0\u12B2-\u12B5\u12B8-\u12BE\u12C0\u12C2-\u12C5\u12C8-\u12D6\u12D8-\u1310\u1312-\u1315\u1318-\u135A\u1380-\u138F\u13A0-\u13F4\u1401-\u166C\u166F-\u167F\u1681-\u169A\u16A0-\u16EA\u1700-\u170C\u170E-\u1711\u1720-\u1731\u1740-\u1751\u1760-\u176C\u176E-\u1770\u1780-\u17B3\u17D7\u17DC\u1820-\u1877\u1880-\u18A8\u18AA\u18B0-\u18F5\u1900-\u191C\u1950-\u196D\u1970-\u1974\u1980-\u19AB\u19C1-\u19C7\u1A00-\u1A16\u1A20-\u1A54\u1AA7\u1B05-\u1B33\u1B45-\u1B4B\u1B83-\u1BA0\u1BAE\u1BAF\u1BBA-\u1BE5\u1C00-\u1C23\u1C4D-\u1C4F\u1C5A-\u1C7D\u1CE9-\u1CEC\u1CEE-\u1CF1\u1CF5\u1CF6\u1D00-\u1DBF\u1E00-\u1F15\u1F18-\u1F1D\u1F20-\u1F45\u1F48-\u1F4D\u1F50-\u1F57\u1F59\u1F5B\u1F5D\u1F5F-\u1F7D\u1F80-\u1FB4\u1FB6-\u1FBC\u1FBE\u1FC2-\u1FC4\u1FC6-\u1FCC\u1FD0-\u1FD3\u1FD6-\u1FDB\u1FE0-\u1FEC\u1FF2-\u1FF4\u1FF6-\u1FFC\u2071\u207F\u2090-\u209C\u2102\u2107\u210A-\u2113\u2115\u2119-\u211D\u2124\u2126\u2128\u212A-\u212D\u212F-\u2139\u213C-\u213F\u2145-\u2149\u214E\u2183\u2184\u2C00-\u2C2E\u2C30-\u2C5E\u2C60-\u2CE4\u2CEB-\u2CEE\u2CF2\u2CF3\u2D00-\u2D25\u2D27\u2D2D\u2D30-\u2D67\u2D6F\u2D80-\u2D96\u2DA0-\u2DA6\u2DA8-\u2DAE\u2DB0-\u2DB6\u2DB8-\u2DBE\u2DC0-\u2DC6\u2DC8-\u2DCE\u2DD0-\u2DD6\u2DD8-\u2DDE\u2E2F\u3005\u3006\u3031-\u3035\u303B\u303C\u3041-\u3096\u309D-\u309F\u30A1-\u30FA\u30FC-\u30FF\u3105-\u312D\u3131-\u318E\u31A0-\u31BA\u31F0-\u31FF\u3400-\u4DB5\u4E00-\u9FCC\uA000-\uA48C\uA4D0-\uA4FD\uA500-\uA60C\uA610-\uA61F\uA62A\uA62B\uA640-\uA66E\uA67F-\uA697\uA6A0-\uA6E5\uA717-\uA71F\uA722-\uA788\uA78B-\uA78E\uA790-\uA793\uA7A0-\uA7AA\uA7F8-\uA801\uA803-\uA805\uA807-\uA80A\uA80C-\uA822\uA840-\uA873\uA882-\uA8B3\uA8F2-\uA8F7\uA8FB\uA90A-\uA925\uA930-\uA946\uA960-\uA97C\uA984-\uA9B2\uA9CF\uAA00-\uAA28\uAA40-\uAA42\uAA44-\uAA4B\uAA60-\uAA76\uAA7A\uAA80-\uAAAF\uAAB1\uAAB5\uAAB6\uAAB9-\uAABD\uAAC0\uAAC2\uAADB-\uAADD\uAAE0-\uAAEA\uAAF2-\uAAF4\uAB01-\uAB06\uAB09-\uAB0E\uAB11-\uAB16\uAB20-\uAB26\uAB28-\uAB2E\uABC0-\uABE2\uAC00-\uD7A3\uD7B0-\uD7C6\uD7CB-\uD7FB\uF900-\uFA6D\uFA70-\uFAD9\uFB00-\uFB06\uFB13-\uFB17\uFB1D\uFB1F-\uFB28\uFB2A-\uFB36\uFB38-\uFB3C\uFB3E\uFB40\uFB41\uFB43\uFB44\uFB46-\uFBB1\uFBD3-\uFD3D\uFD50-\uFD8F\uFD92-\uFDC7\uFDF0-\uFDFB\uFE70-\uFE74\uFE76-\uFEFC\uFF21-\uFF3A\uFF41-\uFF5A\uFF66-\uFFBE\uFFC2-\uFFC7\uFFCA-\uFFCF\uFFD2-\uFFD7\uFFDA-\uFFDC]+/g;
    returnValue = str.replace(r, '');

    //Make sure there are no extra whitespace
    var r2 = /[\s]{2,}/g;
    returnValue = returnValue.replace(r2, ' ');

    //Remove leading and trailing whitespace
    returnValue = returnValue.trim();

    return returnValue;
}

/**
 * Produces a list of column names based on a JsonAdaptedSheetData
 */
function extractImportFileColumnNames(JSonAdaptedSheetData: any): { id: number, name: string }[] {
    const importFileColumnNames: { id: number, name: string }[] = [];
    if (JSonAdaptedSheetData.length !== 0) {

        JSonAdaptedSheetData.forEach((sheetData: any) => {
            var keys = Object.keys(sheetData);

            keys.forEach((key: string) => {
                if (!importFileColumnNames.find((col) => col.name === key)) {
                    const nameStr = normalizeString(key);
                    if (!importFileColumnNames.find((col) => col.name === nameStr)) {
                        const newColumn = { id: importFileColumnNames.length, name: nameStr };
                        importFileColumnNames.push(newColumn);
                    }
                }
            });
        });
    }
    return importFileColumnNames;
}

/**
 * Check if a date string is formatted correctly
 */
function toUtcMidnight(date: Date): Date {
    return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
}

/**
 * IsEmpty checking for yet-to-be-converted string numbers
 */
function isEmpty(val: unknown): boolean {
    return val == null || val === "";
}

/**
 * Produce a validation result for SalesBudgetRows. The result
 * is a key-value dictionary where the key is the field being validated
 * and the value is the validation message
 */
function validateSalesBudgetRows(rows: SalesBudgetRow[], validLocationNumbers: number[], t: (key: string) => string): BudgetRowsValidationResult[] {
    const results: BudgetRowsValidationResult[] = [];

    const add = (columnName: string, description: string) => {
        if (!results.find((r) => r.ColumnName === columnName)) {
            results.push({ ColumnName: columnName, ErrorDescription: description });
        }
    };

    const invalidLocationNumbers = new Set<string>();
    const seenDateLocationKeys = new Set<string>();
    const duplicateStartDates = new Set<string>();

    rows.forEach((row) => {
        if (!row.startDate) add("startDate", t("budgetimport.errors.dateEmpty"));
        if (row.locationNumber && validLocationNumbers.length > 0 && !validLocationNumbers.includes(Number(row.locationNumber))) {
            invalidLocationNumbers.add(String(row.locationNumber));
        }
        if (row.startDate) {
            const dateStr = row.startDate.toISOString().split('T')[0];
            const dateLocationKey = `${dateStr}|${row.locationNumber}`;
            if (seenDateLocationKeys.has(dateLocationKey)) {
                duplicateStartDates.add(dateStr);
            } else {
                seenDateLocationKeys.add(dateLocationKey);
            }
        }
    });

    if (invalidLocationNumbers.size > 0) {
        results.push({ ColumnName: "locationNumber", ErrorDescription: `${t("budgetimport.errors.invalidLocationNumbers")}: ${Array.from(invalidLocationNumbers).join(", ")}` });
    }
    if (duplicateStartDates.size > 0) {
        results.push({ ColumnName: "startDate", ErrorDescription: `${t("budgetimport.errors.duplicateDates")}: ${Array.from(duplicateStartDates).join(", ")}` });
    }

    return results;
}

/**
 * Produce a validation result for PurchaseBudgetRows. The result
 * is a key-value dictionary where the key is the field being validated
 * and the value is the validation message
 */
function validatePurchaseBudgetRows(rows: PurchaseBudgetRow[], validItemGroupCodes: string[], validLocationNumbers: number[], validCollectionCodes: number[], itemGroupCodeMapped: boolean, t: (key: string) => string): BudgetRowsValidationResult[] {
    const results: BudgetRowsValidationResult[] = [];

    const add = (columnName: string, description: string) => {
        if (!results.find((r) => r.ColumnName === columnName)) {
            results.push({ ColumnName: columnName, ErrorDescription: description });
        }
    };

    const invalidItemGroupCodes = new Set<string>();
    const invalidLocationNumbers = new Set<string>();
    const invalidCollectionCodes = new Set<string>();
    const emptyItemGroupCodeRows: number[] = [];

    rows.forEach((row, index) => {
        if (!row.collectionCode) add("collectionCode", t("budgetimport.errors.collectioncodeEmpty"));
        if (row.collectionCode && validCollectionCodes.length > 0 && !validCollectionCodes.includes(Number(row.collectionCode))) {
            invalidCollectionCodes.add(String(row.collectionCode));
        }
        if (!itemGroupCodeMapped) add("itemGroupCode", t("budgetimport.errors.itemGroupCodeNotMapped"));
        else if (!row.itemGroupCode) emptyItemGroupCodeRows.push(index + 1);
        if (row.itemGroupCode && validItemGroupCodes.length > 0 && !validItemGroupCodes.includes(row.itemGroupCode)) {
            invalidItemGroupCodes.add(row.itemGroupCode);
        }
        if (!row.locationNumber) add("locationNumber", t("budgetimport.errors.locationNumberEmpty"));
        if (row.locationNumber && validLocationNumbers.length > 0 && !validLocationNumbers.includes(Number(row.locationNumber))) {
            invalidLocationNumbers.add(String(row.locationNumber));
        }
        if (isEmpty(row.quantity)) add("quantity", t("budgetimport.errors.quantityEmpty"));
        else if (isNaN(Number(row.quantity))) add("quantity", t("budgetimport.errors.invalidQuantityFormat"));
        else if (Number(row.quantity) <= 0) add("quantity", t("budgetimport.errors.quantityMustBeGreaterThanZero"));
        if (isEmpty(row.purchaseCost)) add("purchaseCost", t("budgetimport.errors.purchaseCostEmpty"));
        else if (isNaN(Number(row.purchaseCost))) add("purchaseCost", t("budgetimport.errors.invalidPurchaseCostFormat"));
        else if (Number(row.purchaseCost) < 0) add("purchaseCost", t("budgetimport.errors.purchaseCostCannotBeNegative"));
    });

    if (invalidCollectionCodes.size > 0) {
        results.push({ ColumnName: "collectionCode", ErrorDescription: `${t("budgetimport.errors.invalidCollectionCodes")}: ${Array.from(invalidCollectionCodes).join(", ")}` });
    }
    if (emptyItemGroupCodeRows.length > 0) {
        results.push({ ColumnName: "itemGroupCode", ErrorDescription: `${t("budgetimport.errors.itemGroupCodeEmpty")} - ${t("budgetimport.rows")}: ${emptyItemGroupCodeRows.join(", ")}` });
    }
    if (invalidItemGroupCodes.size > 0) {
        results.push({ ColumnName: "itemGroupCode", ErrorDescription: `${t("budgetimport.errors.invalidItemGroupCodes")}: ${Array.from(invalidItemGroupCodes).join(", ")}` });
    }
    if (invalidLocationNumbers.size > 0) {
        results.push({ ColumnName: "locationNumber", ErrorDescription: `${t("budgetimport.errors.invalidLocationNumbers")}: ${Array.from(invalidLocationNumbers).join(", ")}` });
    }

    return results;
}

